import type { Client as OpenFeatureClient } from '@openfeature/web-sdk';

import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature, ProviderEvents } from '@openfeature/web-sdk';
import kebabCase from 'lodash/kebabCase';
import { defineStore } from 'pinia';
import { computed, type Ref, ref, watch } from 'vue';

import { env } from '@/env';
import { useIdentityStore } from '@/stores/identity';
import { isE2E } from '@/utils/e2e';

declare global {
  interface Window {
    __e2eMockedFlags__?: Record<string, boolean>;
  }
}

export type FlagValues = {
  /**
   * Temporary local override used across the app.
   */
  internalPoc: boolean;
  /**
   * Most flags in Dash are boolean LD variations; treat unknown keys as boolean/undefined.
   */
  [key: string]: boolean | undefined;
};

export const createFlagsProxy = (base: Partial<FlagValues> = {}): FlagValues =>
  new Proxy(
    {
      internalPoc: false,
      ...base,
    } as FlagValues,
    {
      get(target, prop, receiver) {
        // Vue probes `__v_*` on anything it makes reactive, and symbols are never flag
        // keys. Answering the flag default for `__v_skip` disabled reactivity outright.
        if (typeof prop !== 'string' || prop.startsWith('__v_')) {
          return Reflect.get(target, prop, receiver);
        }

        // Matches production, where `createVariationFlagsProxy` resolves an unset flag
        // through `getBooleanValue(flagKey, false)`. Defaulting to `true` meant an
        // undeclared flag exercised the ON path while production ran OFF — sc-208210.
        const value = Reflect.get(target, prop, receiver);
        return value ?? false;
      },
    },
  );

export const useFlagStore = defineStore('flag', () => {
  const identityStore = useIdentityStore();

  let waitForUserFlagsResolve: () => void;
  const waitForUserFlags = new Promise<void>((resolve) => {
    waitForUserFlagsResolve = resolve;
  });

  const initialized = ref(false);
  const ready = ref(false);
  const userFlagsReady = ref(false);
  const internalFlags = ref({} as FlagValues);
  const clientError: Ref<unknown> = ref(null);
  const isMocked = ref(false);
  const flagsMock = ref<FlagValues | null>(null);
  let client: OpenFeatureClient | null = null;

  function createVariationFlagsProxy(): FlagValues {
    return new Proxy({} as FlagValues, {
      get(target, prop, receiver) {
        // Vue uses this to determine if this is a ref; we must return the real value.
        if (prop === '__v_isRef') {
          return Reflect.get(target, prop, receiver);
        }
        if (typeof prop !== 'string') {
          return Reflect.get(target, prop, receiver);
        }

        const flagKey = kebabCase(prop);
        // To temporarily set a flag for local development, add it here as follows:
        // return flagKey === 'name-of-my-flag' ? true : client?.getBooleanValue(flagKey, false);
        return client?.getBooleanValue(flagKey, false);
      },
    });
  }

  const flags = computed<FlagValues>({
    get() {
      return isMocked.value ? (flagsMock.value as FlagValues) : internalFlags.value;
    },
    set(v) {
      if (isMocked.value) {
        flagsMock.value = v;
      } else {
        internalFlags.value = v;
      }
    },
  });

  function userContext() {
    const identity = identityStore.identity as {
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      organization?: { label: string };
    } | null;
    if (identity) {
      const ctx: Record<string, string> = {
        targetingKey: String(identity.id),
        kind: 'user',
        key: String(identity.id),
        email: identity.email,
        name: `${identity.first_name} ${identity.last_name}`,
      };
      if (identityStore.currentBrand?.label) {
        ctx.brand = identityStore.currentBrand.label;
      }
      if (identity.organization?.label) {
        ctx.organization = identity.organization.label;
      }
      return ctx;
    }
    return {
      targetingKey: 'anonymous',
      kind: 'user',
      key: 'anonymous',
      email: 'test+anonuser@dashhudson.com',
      name: 'Anonymous User',
    };
  }

  function mockFlags(obj: Partial<FlagValues> = {}) {
    ready.value = true;
    waitForUserFlagsResolve();
    userFlagsReady.value = true;
    flagsMock.value = createFlagsProxy(obj);
    isMocked.value = true;
  }
  function clearFlagsMock() {
    flagsMock.value = null;
    isMocked.value = false;
  }

  function createFlagClient() {
    if (!client) {
      clearFlagsMock();

      const context = userContext();
      const provider = new DatadogProvider({
        clientToken: env.datadogClientToken,
        site: 'datadoghq.com',
        applicationId: env.datadogApplicationId,
        env: env.mode,
        service: 'dash-web',
        version: env.revision,
        enableExposureLogging: true,
        ...(env.datadogFlaggingProxy && { flaggingProxy: env.datadogFlaggingProxy }),
      });

      void OpenFeature.setContext(context);

      OpenFeature.setProvider(provider);
      client = OpenFeature.getClient();

      client.addHandler(ProviderEvents.Ready, () => {
        internalFlags.value = createVariationFlagsProxy();
        ready.value = true;
      });
      client.addHandler(ProviderEvents.ConfigurationChanged, () => {
        internalFlags.value = createVariationFlagsProxy();
      });
      client.addHandler(ProviderEvents.Error, (e) => {
        clientError.value = e;
        // Resolve rather than reject to match fail-open design — the app
        // should continue with default flag values rather than blocking navigation.
        waitForUserFlagsResolve();
      });
    }
  }

  async function updateIdentity() {
    if (!client) return;
    userFlagsReady.value = false;
    const ctx = userContext();
    await OpenFeature.setContext(ctx);
    if (ctx.targetingKey !== 'anonymous') {
      userFlagsReady.value = true;
      waitForUserFlagsResolve();
    }
  }

  function e2eMockFlags(obj: Record<string, boolean>) {
    ready.value = true;
    waitForUserFlagsResolve();
    userFlagsReady.value = true;
    // Not `createFlagsProxy`: unknown keys must stay `undefined`, because
    // `mixins/ldRedirect.js` reads `flags[key] !== undefined` to tell "not loaded" from
    // "off". Collapsing the two would make every e2e spec redirect on an undeclared flag.
    flagsMock.value = new Proxy({ ...obj } as FlagValues, {
      get(target, prop, receiver) {
        return Reflect.get(target, prop, receiver) as boolean | undefined;
      },
    });
    isMocked.value = true;
  }

  watch(
    () => identityStore.identity,
    () => {
      if (isE2E() && window.__e2eMockedFlags__) {
        e2eMockFlags(window.__e2eMockedFlags__);
      } else {
        createFlagClient();
        // Re-evaluate the flag context when the identity itself changes, not
        // only on brand switches — shared (no-login) sessions never get a
        // currentBrand, so without this userFlagsReady stays false for them.
        void updateIdentity();
      }
    },
    {
      immediate: true,
    },
  );

  watch(
    () => identityStore.currentBrand,
    () => {
      void updateIdentity();
    },
    {
      immediate: true,
    },
  );

  function updateFlags({
    ready: incomingReady = false,
    flags: incomingFlags = {} as FlagValues,
  }: {
    ready?: boolean;
    flags?: FlagValues;
  } = {}) {
    ready.value = incomingReady;
    if (incomingReady) {
      initialized.value = true;
      internalFlags.value = incomingFlags;
    }
  }

  return {
    initialized,
    ready,
    userFlagsReady,
    flags,
    clientError,
    updateFlags,
    waitForUserFlags,
    mockFlags,
    isMocked,
    clearFlagsMock,
  };
});
